/*
  Meta CAPI helper for web (Pixel + Server) deduplication.
  - Collects fbp/fbc from cookies (and creates _fbc from fbclid when present)
  - Generates event_id
  - Sends payload to backend endpoint: /meta-capi-web/event/send
  - Calls fbq('track', ...) with eventID for dedup with browser Pixel
*/

import ReactPixel from "react-facebook-pixel";
import { v4 as uuidv4 } from "uuid";
import { isDev } from "config";
import { axiosInstance, CustomResponseFormat } from "../api";
import { getAdChannel } from "routes/adFunnel";

const FBP_COOKIE = "_fbp";
const FBC_COOKIE = "_fbc";

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(name + "="));
  if (!match) return undefined;
  try {
    const raw = decodeURIComponent(match.split("=")[1]);
    // Normalize empty/invalid persisted values
    if (!raw || raw === "null" || raw === "undefined") return undefined;
    return raw;
  } catch {
    const raw = match.split("=")[1];
    if (!raw || raw === "null" || raw === "undefined") return undefined;
    return raw;
  }
}

function setCookie(name: string, value: string, days = 90) {
  if (typeof document === "undefined") return;
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
}

function getQueryParam(param: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const url = new URL(window.location.href);
  const v = url.searchParams.get(param);
  return v ?? undefined;
}

/**
 * Ensure _fbc cookie exists using fbclid from URL when available.
 * Standard value format: fb.1.<timestamp_ms>.<fbclid>
 */
export function ensureFbcFromUrl(): string | undefined {
  const currentFbc = getCookie(FBC_COOKIE);
  // Validate existing _fbc format: fb.1.<timestamp>.<id>
  const fbcPattern = /^fb\.1\.\d+\..+/;

  // Meta 공식: URL에 새 fbclid가 보이면 _fbc를 갱신한다 (last-click, 픽셀 자체 동작과 동일).
  // 같은 fbclid면 기존 쿠키(최초 관측 creationTime)를 유지한다.
  const fbclid = getQueryParam("fbclid");
  if (fbclid) {
    const currentClickId =
      currentFbc && fbcPattern.test(currentFbc)
        ? currentFbc.split(".").slice(3).join(".")
        : undefined;
    if (currentFbc && currentClickId === fbclid) return currentFbc;

    const fbc = `fb.1.${Date.now()}.${fbclid}`;
    setCookie(FBC_COOKIE, fbc);
    return fbc;
  }

  if (currentFbc && fbcPattern.test(currentFbc)) return currentFbc;

  // Some links may carry fbc directly
  const fbcFromQuery = getQueryParam("fbc");
  if (fbcFromQuery && fbcPattern.test(fbcFromQuery)) {
    setCookie(FBC_COOKIE, fbcFromQuery);
    return fbcFromQuery;
  }

  return undefined;
}

/**
 * Ensure _fbp cookie exists. If missing, create one with the standard format:
 * fb.1.<timestamp_ms>.<random>
 * Returns the existing or newly created value.
 */
export function ensureFbp(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const existing = getCookie(FBP_COOKIE);
  const fbpPattern = /^fb\.1\.\d+\..+/;
  if (existing && fbpPattern.test(existing)) return existing;

  const ts = Date.now();
  // 10- to 13-digit random segment similar to Pixel's behavior
  const rand = Math.floor(Math.random() * 1e10);
  const fbp = `fb.1.${ts}.${rand}`;
  setCookie(FBP_COOKIE, fbp);
  return fbp;
}

export function getFbp(): string | undefined {
  return getCookie(FBP_COOKIE);
}

export function getFbc(): string | undefined {
  return getCookie(FBC_COOKIE);
}

// 서버에 마지막으로 동기화 성공한 (externalId|fbc) 페어 — 같은 값 재전송 방지
const FBC_SYNCED_STORAGE_KEY = "fbc_synced";

// 동일 페어 동시 중복 전송 방지 (StrictMode 이중 실행 등, 페이지 세션 단위)
let fbcSyncInFlightKey: string | null = null;

/**
 * 로그인된 계정에 현재 fbc(last-click)를 서버로 동기화한다.
 * 가입 등 전환 이벤트 경로는 서버 CAPI 릴레이가 fbc를 자체 저장하지만, 재로그인·기로그인
 * 재방문은 CAPI 이벤트가 없어 서버 저장 fbc가 갱신되지 않는다 — 그 공백을 메우는 함수.
 * fbc 쿠키가 없으면(인앱 웹뷰 등) 아무것도 하지 않는다. 전송은 fire-and-forget.
 */
export function syncFbcToAccount(externalId?: number | string): void {
  // gads 퍼널은 Meta 채널을 쓰지 않는다 (routes/adFunnel.ts)
  if (getAdChannel() === "google") return;
  const externalIdString =
    externalId !== undefined && externalId !== null && String(externalId).trim()
      ? String(externalId)
      : undefined;
  if (!externalIdString) return;

  // URL에 새 fbclid가 있으면 쿠키를 last-click으로 갱신한 뒤 그 값을 동기화
  const fbc = ensureFbcFromUrl();
  if (!fbc) return;

  const key = `${externalIdString}|${fbc}`;
  if (fbcSyncInFlightKey === key) return;
  try {
    if (localStorage.getItem(FBC_SYNCED_STORAGE_KEY) === key) return;
  } catch {
    // localStorage 접근 불가 시 가드 없이 전송 (서버 upsert 는 멱등)
  }

  fbcSyncInFlightKey = key;
  axiosInstance
    .post<CustomResponseFormat<{ captured?: boolean }>>(
      "/ad/meta-capi-web/fbc/sync",
      { fbc, externalId: externalIdString }
    )
    .then((response) => {
      // 서버가 실제로 저장했을 때만 완료 마킹 — 거부(검증 실패·복제 지연 등)면 다음 기회에 재시도
      if (response?.code === 0 && response?.result?.captured === true) {
        try {
          localStorage.setItem(FBC_SYNCED_STORAGE_KEY, key);
        } catch {
          // ignore
        }
      } else if (fbcSyncInFlightKey === key) {
        fbcSyncInFlightKey = null;
      }
    })
    .catch(() => {
      // 실패 시 다음 기회에 재시도할 수 있도록 가드 해제 (다른 키가 전송 중이면 건드리지 않음)
      if (fbcSyncInFlightKey === key) {
        fbcSyncInFlightKey = null;
      }
    });
}

/*
  Pixel Advanced Matching (EMQ)
  - 픽셀의 매칭 데이터(em/external_id)는 fbq('init') 로만 전달되는 페이지 세션 메모리 값이라
    새로고침·재방문 시 사라진다. 아래 유틸이 3중으로 복원한다:
    (1) App.tsx 최초 init 에 저장된 로그인 사용자를 탑재 (PageView 부터 적용)
    (2) trackCapiStandardEvent 가 전환 직전 재보장 (인자 우선, 저장 사용자로 보완)
    (3) CAPI userData 는 항상 서버로 전송 (브라우저 저장소와 무관한 안전망)
  - 단일 가드(appliedAdvancedMatchingKey)로 같은 식별자 재-init 을 건너뛴다.
    init 은 이벤트를 발생시키지 않으므로 이벤트 중복도 없다.
*/

// dev/real 웹 픽셀 ID 단일 출처 (App.tsx 초기화와 CAPI dedup 이 같은 픽셀을 쓴다)
export const FB_PIXEL_ID = isDev ? "864462499876586" : "891312099992669";

// recoil/userAtom 의 localStorage persistence 키
const USER_STORAGE_KEY = "user";

export interface PixelAdvancedMatching {
  em?: string;
  external_id?: string;
}

// 같은 식별자로의 중복 재-init 방지 (페이지 세션 단위)
let appliedAdvancedMatchingKey: string | null = null;

function normalizeAdvancedMatching(
  email?: unknown,
  externalId?: unknown
): PixelAdvancedMatching {
  const em =
    typeof email === "string" && email.trim()
      ? email.trim().toLowerCase()
      : undefined;
  const externalIdString =
    externalId !== undefined && externalId !== null && String(externalId).trim()
      ? String(externalId)
      : undefined;
  return {
    ...(em ? { em } : {}),
    ...(externalIdString ? { external_id: externalIdString } : {}),
  };
}

/**
 * localStorage 에 영속된 로그인 사용자(recoil/userAtom)로부터 매칭 데이터 복원.
 * 쿠키/픽셀 메모리가 초기화돼도 로그인 상태면 되살릴 수 있다.
 */
export function getStoredUserAdvancedMatching():
  | PixelAdvancedMatching
  | undefined {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return undefined;
    const user = JSON.parse(raw);
    const data = normalizeAdvancedMatching(user?.email, user?.userId);
    return data.em || data.external_id ? data : undefined;
  } catch {
    return undefined;
  }
}

/**
 * 최초 ReactPixel.init 에 Advanced Matching 을 실어 보낸 뒤 호출 —
 * 동일 식별자에 대한 불필요한 재-init 을 건너뛰도록 가드를 동기화한다.
 */
export function markAdvancedMatchingApplied(data?: PixelAdvancedMatching) {
  if (data && (data.em || data.external_id)) {
    appliedAdvancedMatchingKey = `${data.em ?? ""}|${data.external_id ?? ""}`;
  }
}

/**
 * 픽셀에 Advanced Matching(em/external_id) 적용 — fbq('init') 재호출(Meta 공식 경로).
 * 인자 값이 우선이고 빠진 필드는 저장된 로그인 사용자로 보완한다.
 * 동일 식별자면 재-init 하지 않으며, init 은 이벤트를 만들지 않는다.
 */
export function setPixelAdvancedMatching(
  email?: unknown,
  externalId?: unknown
) {
  const fromArgs = normalizeAdvancedMatching(email, externalId);
  const stored =
    !fromArgs.em || !fromArgs.external_id
      ? getStoredUserAdvancedMatching()
      : undefined;
  const data: PixelAdvancedMatching = { ...(stored ?? {}), ...fromArgs };
  if (!data.em && !data.external_id) return;

  const key = `${data.em ?? ""}|${data.external_id ?? ""}`;
  if (key === appliedAdvancedMatchingKey) return;

  try {
    ReactPixel.fbq("init", FB_PIXEL_ID, data);
    appliedAdvancedMatchingKey = key;
  } catch (e) {
    // 픽셀 미로드 등 — CAPI userData 가 서버측에서 매칭을 보완
  }
}

export interface TrackOptions {
  eventId?: string;
  eventSourceUrl?: string;
}

export interface CapiPayload {
  eventName: string;
  eventTime: number; // Unix timestamp (sec)
  eventId: string;
  eventSourceUrl?: string;
  userData: {
    clientUserAgent?: string;
    fbp?: string;
    fbc?: string;
    external_id?: string;
    em?: string;
  } & Record<string, unknown>;
  customData?: Record<string, unknown>;
  version?: string;
}

export async function trackCapiStandardEvent(
  eventName: string,
  eventParams?: Record<string, unknown>,
  options?: TrackOptions,
  version?: string, // version
  isAppEnv?: boolean
): Promise<{ eventId: string } | void> {
  // gads 퍼널(Google Ads 전용)에서는 픽셀·CAPI·fbc 쿠키 모두 건드리지 않는다 (routes/adFunnel.ts)
  if (getAdChannel() === "google") return;
  try {
    if (!isAppEnv) {
      ensureFbcFromUrl();
    }
    // Proactively ensure _fbp exists so that Contact or early-funnel events carry fbp
    ensureFbp();

    const eventId = options?.eventId ?? uuidv4();
    const fbp = getFbp();
    const fbc = getFbc();
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : undefined;
    const eventSourceUrl =
      options?.eventSourceUrl ??
      (typeof window !== "undefined" ? window.location.href : undefined);

    // Extract user data for Advanced Matching
    const externalId = eventParams?.externalId
      ? String(eventParams.externalId)
      : undefined;
    const email = eventParams?.email ? String(eventParams.email) : undefined;

    // Separate customData from userData fields
    const {
      externalId: _externalId,
      email: _email,
      ...customData
    } = eventParams || {};

    // 픽셀측 EMQ: 전환 직전 Advanced Matching 재보장
    // (새로고침 등으로 픽셀 메모리가 초기화된 경우 저장된 로그인 사용자로 복원)
    setPixelAdvancedMatching(email, externalId);

    // Call Pixel with eventID for deduplication.
    // PII(email/externalId)는 픽셀 custom_data로 보내지 않는다 — CAPI userData로만 전달.
    try {
      ReactPixel.fbq("track", eventName, customData, {
        eventID: eventId,
      });
    } catch (e) {
      // fallback to standard track (without eventID) if fbq call fails
      // ReactPixel.track(eventName, eventParams || {});
    }

    // Only include defined userData fields
    const userData: CapiPayload["userData"] = {
      clientUserAgent: userAgent,
      ...(fbp ? { fbp } : {}),
      ...(fbc ? { fbc } : {}),
      ...(externalId ? { externalId: externalId } : {}),
      ...(email ? { em: email } : {}),
    };

    const payload: CapiPayload = {
      eventName: eventName,
      eventTime: Math.floor(Date.now() / 1000),
      eventId: eventId,
      eventSourceUrl: eventSourceUrl,
      userData,
      customData,
      version: version,
    };

    // Fire-and-forget; do not block user interactions
    axiosInstance
      .post("/ad/meta-capi-web/event/send", payload)
      .catch(() => void 0);

    return { eventId };
  } catch (err) {
    // Best-effort: still try to send pixel event even if CAPI prep fails
    try {
      // ReactPixel.track(eventName, eventParams || {});
    } catch (_) {
      // ignore
    }
  }
}
